import type { NativeBridge } from '@tbcast/shared/native';

function dataUrlToBlob(dataUrl: string): Blob {
  const [head, b64] = dataUrl.split(',');
  const mime = /data:([^;]+)/.exec(head ?? '')?.[1] ?? 'image/jpeg';
  const bin = atob(b64 ?? '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Native screenshot (agent/Electron: real screen incl. video) with html2canvas
 * as the browser fallback (DOM render; video frames are drawn where possible).
 */
export async function captureScreenshot(
  native: NativeBridge | null,
  root: HTMLElement,
): Promise<Blob | null> {
  const nativeShot = await native?.screenshot().catch(() => null);
  if (nativeShot) return dataUrlToBlob(nativeShot);
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(root, {
      backgroundColor: '#000',
      scale: Math.min(1, 1280 / window.innerWidth),
      useCORS: true,
      logging: false,
    });
    // Paint current video frames on top (html2canvas leaves them blank).
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const scale = canvas.width / window.innerWidth;
      for (const v of Array.from(document.querySelectorAll('video'))) {
        const r = v.getBoundingClientRect();
        if (r.width && v.readyState >= 2 && getComputedStyle(v).visibility !== 'hidden') {
          try {
            ctx.drawImage(v, r.left * scale, r.top * scale, r.width * scale, r.height * scale);
          } catch {
            // tainted cross-origin frame
          }
        }
      }
    }
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8));
  } catch {
    return null;
  }
}
